import Box from '@mui/material/Box';
import { Formik } from 'formik';
import * as yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { CountryCode, isValidPhoneNumber } from 'libphonenumber-js';
import { getCountryCodes, preventSpecialCharacters } from '../../../utils/utilityFunctions';
import { investorSignUpApi, loginWithOtp } from '../../../redux-store/actions/auth';
import {
  InvestorVerificationResponse,
  LoginWithOtpResponseBody,
} from '../../../redux-store/types/api-types';
import { CountryCodesDropDown, LoginLayout } from '../../commonComponents';
import { MFSubmitButton, MFTextField } from '../../../lib/formik';
import { emailRegex, nonDigitRegex } from '../../../utils/regex';
import { Button, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { RootStateType } from '../../../redux-store/reducers';

interface Values {
  phone: string;
  countryCode: string;
  countryNameAndCode: string;
  email: string;
}

const validationSchema = yup.object().shape({
  phone: yup.string().test('phone', (value, context) => {
    const { createError, path, parent } = context;
    const codesList = getCountryCodes()
      .map((codes) => {
        if (codes.label === parent.countryNameAndCode) {
          return codes.countryCode;
        }
      })
      .filter((ele) => ele)
      .toString();
    if (!value) {
      return createError({
        message: 'Mobile number is required',
        path,
      });
    }
    if (
      !isValidPhoneNumber(
        parent?.countryNameAndCode?.split(':')[1].trim() + value,
        codesList as CountryCode
      )
    ) {
      return createError({
        message: 'Invalid Mobile number',
        path,
      });
    }
    return true;
  }),
  countryNameAndCode: yup
    .string()
    .nullable()
    .test('countryNameAndCode', (value, context) => {
      const { createError, path, parent } = context;
      const codesList = getCountryCodes()
        .map((codes) => {
          if (codes.label === value) {
            return codes.countryCode;
          }
        })
        .filter((ele) => ele)
        .toString();
      if (!value) {
        return createError({
          message: 'Country Code is required',
          path,
        });
      }
      if (
        parent.phone &&
        !isValidPhoneNumber(value?.split(':')[1].trim() + parent.phone, codesList as CountryCode)
      ) {
        return createError({
          message: 'Invalid Mobile number',
          path,
        });
      }

      return true;
    }),
  email: yup
    .string()
    .nullable()
    .test('email', (value, context) => {
      const { createError, path } = context;
      if (!value) {
        return createError({
          message: 'Email ID is required',
          path,
        });
      }
      if (!emailRegex.test(value)) {
        return createError({
          message: 'Invalid Email ID',
          path,
        });
      }
      return true;
    }),
});

export default function InvestorSignUp(): JSX.Element {
  const dispatch = useDispatch();
  const history = useHistory();
  const token =
    new URLSearchParams(window.location.search).get('token') ||
    useSelector((store: RootStateType) => store.preventLogoutReducer.token);
  const arn_link = new URLSearchParams(window.location.search).get('arn_link') || '';
  const onSubmit = async (values: Values) => {
    try {
      const {
        phone = '',
        countryCode = '+91',
        countryNameAndCode = 'India: +91',
        email = '',
      } = values;
      const res = (await dispatch(
        investorSignUpApi({
          phone: phone.trim(),
          countryCode: countryNameAndCode?.split(':')[1].trim(),
          email: email.trim(),
          ...(token && { token: token.toString() }),
          ...(arn_link && { arn_link }),
        })
      )) as unknown as InvestorVerificationResponse;
      console.log(res, 'signup');
      history.push('verify-otp', {
        countryCode: countryNameAndCode?.split(':')[1].trim(),
        loginType: 'investor_signup',
        ...(res || {}),
        phone: phone.trim(),
        ...(token && { token: token.toString() }),
        ...(arn_link && { arn_link }),
      });
    } catch (e) {
      console.error((e as unknown as Error).message);
    }
  };
  const initialValues: Values = {
    phone: '',
    countryCode: '+91',
    countryNameAndCode: 'India: +91',
    email: '',
  };

  return (
    // <LoginLayout LoginText="Investor Signup">
    <Formik onSubmit={onSubmit} initialValues={initialValues} validationSchema={validationSchema}>
      {({ handleSubmit, values }) => (
        <Box
          component="form"
          noValidate
          onSubmit={handleSubmit}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            '.MuiInputLabel-root': {
              fontFamily: 'Poppins, sans-serif',
              fontSize: 13,
              fontWeight: 500,
              color: '#4a5568',
            },
            '.MuiOutlinedInput-root': {
              borderRadius: '11px',
              backgroundColor: '#f8faff',
              fontSize: 15,
              '& fieldset': {
                borderColor: '#e2e8f0',
                borderWidth: '1.5px',
              },
              '&:hover fieldset': { borderColor: '#a0aec0' },
              '&.Mui-focused fieldset': {
                borderColor: 'primary.main',
                borderWidth: '1.5px',
              },
              '&.Mui-focused': {
                backgroundColor: '#fff',
                boxShadow: '0 0 0 3px rgba(59,107,214,0.10)',
              },
            },
            '.MuiFormControl-root': {
              position: 'relative',
              mb: '28px',
            },
            '.MuiFormHelperText-root': {
              position: 'absolute',
              bottom: '-18px',
              margin: 0,
              fontSize: 12,
            },
          }}>
          <MFTextField
            label="Mobile Number"
            name="phone"
            placeholder="Enter Mobile number"
            onKeyDown={(e) => preventSpecialCharacters(e)}
            startAdornment={
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexShrink: 0,
                  '.MuiOutlinedInput-notchedOutline': { display: 'none' },
                  '.MuiOutlinedInput-root': {
                    backgroundColor: 'transparent !important',
                    boxShadow: 'none !important',
                    pr: 0,
                  },
                  '.MuiSelect-select': {
                    py: '0 !important',
                    pl: '0 !important',
                    fontSize: 14,
                    fontFamily: 'Poppins, sans-serif',
                    fontWeight: 500,
                  },
                }}>
                <CountryCodesDropDown
                  name={'countryNameAndCode'}
                  value={values.countryNameAndCode}
                />
                <Box sx={{ width: '1px', height: '20px', bgcolor: '#e2e8f0' }} />
              </Box>
            }
            autoFocus
            type="number"
            regexForFilterValue={nonDigitRegex}
          />
          <MFTextField
            required
            label="Enter Email ID"
            name="email"
            type="text"
            placeholder="Enter Email ID"
            autoComplete="off"
          />
          <MFSubmitButton label="Get OTP" />

          {/* ✅ Added login link */}
          <Typography
            sx={{ textAlign: 'center', fontSize: 14, color: 'text.secondary', mt: '16px' }}>
            Already an investor?&nbsp;
            <Button
              onClick={() =>
                history.push({
                  pathname: '/investor-login',
                  search:
                    arn_link && token
                      ? `?token=${token}&arn_link=${arn_link}`
                      : token
                      ? `?token=${token}`
                      : arn_link
                      ? `?arn_link=${arn_link}`
                      : '',
                })
              }
              sx={{
                fontWeight: 600,
                fontSize: 14,
                textTransform: 'none',
                color: 'primary.main',
                p: 0,
                minWidth: 'auto',
                '&:hover': {
                  textDecoration: 'underline',
                  backgroundColor: 'transparent',
                },
              }}>
              Login here
            </Button>
          </Typography>
        </Box>
      )}
    </Formik>
    // </LoginLayout>
  );
}
